import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  FaUsers, 
  FaUserPlus, 
  FaDownload, 
  FaChevronLeft, 
  FaChevronRight, 
  FaTrash, 
  FaUserEdit,
  FaSearch,
  FaTimes,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaProjectDiagram,
  FaChartLine,
  FaChevronDown,
  FaChevronUp,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import { collection, getDocs, deleteDoc, doc, query, where, getDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './EmployeeAnalysis.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../components/TableFilter';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useNavigate, Navigate } from 'react-router-dom';
import ProjectVisitsModal from '../pages/ProfilePage/ProjectVisitsModal';
import FTEModal from './EmployeeAnalysis/FTEModal';
import { useTheme } from '../contexts/ThemeContext';
import './EmployeeAnalysisDarkMode.css';

const safeToString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).toLocaleLowerCase('tr-TR');
};

const TariffDetails = ({ worker, isDarkMode }) => {
  if (!worker || !worker.projects || worker.projects.length === 0) {
    return (
      <div className={`${styles.tariffDetailsContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
        <p>Bu çalışan için tarife bilgisi bulunamadı.</p>
      </div>
    );
  }

  // Get all projects with rates
  const projectsWithRates = worker.projects.filter(project => project.rate);

  return (
    <div className={`${styles.tariffDetailsContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <h4 className={styles.tariffDetailsTitle}>Tarife Detayları</h4>
      
      {/* Display project rates */}
      <div className={styles.tariffProjectList}>
        {projectsWithRates.map((project, index) => (
          <div key={index} className={styles.tariffProjectItem}>
            <div className={styles.tariffProjectName}>
              <strong>{project.projectTitle}</strong> ({project.role})
            </div>
            <div className={styles.tariffProjectRate}>
              {project.rate.type}: {project.rate.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EmployeeAnalysis = () => {
  const { isDarkMode } = useTheme();
  const { employeeId, employeeData, userAuthorization } = useUserProfile();
  const navigate = useNavigate();
  
  // Check if user is admin
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');
  
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insurances, setInsurances] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredInsurances, setFilteredInsurances] = useState([]);
  const [filters, setFilters] = useState({});
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  const [showTariffDetails, setShowTariffDetails] = useState(false);
  const [showFTEModal, setShowFTEModal] = useState(false);
  const [selectedEmployeeFTE, setSelectedEmployeeFTE] = useState(null);
  const [fteTariffs, setFteTariffs] = useState([]);
  const [loadingFTE, setLoadingFTE] = useState(false);
  const [visitCounts, setVisitCounts] = useState({});
  const [expenses, setExpenses] = useState({});
  const [payments, setPayments] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef(null);
  const [activeProjects, setActiveProjects] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [bulkDataLoading, setBulkDataLoading] = useState(false);

  // Gruplu tablo: hangi çalışan satırlarının açık olduğu
  const [expandedWorkers, setExpandedWorkers] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'name', dir: 'asc' });

  const fetchInsurances = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Insurances'));
      const insuranceData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setInsurances(insuranceData);
    } catch (error) {
      console.error('Error fetching insurances:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      // Önce tüm sigortaları getir
      const insuranceSnapshot = await getDocs(collection(db, 'Insurances'));
      const insurances = insuranceSnapshot.docs.map(doc => doc.data());
      
      // Sonra çalışanları getir
      const employeeSnapshot = await getDocs(collection(db, 'Employees'));
      const employeeData = employeeSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          eName: doc.data().eName,
          eSurname: doc.data().eSurname
        }))
        // Sigortası olmayan çalışanları filtrele
        .filter(employee => {
          // Çalışanın sigortası var mı kontrol et
          const hasInsurance = insurances.some(
            insurance => insurance.employeeId === employee.id
          );
          // Sigortası yoksa true döndür (listede göster)
          return !hasInsurance;
        });

      setEmployees(employeeData);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  useEffect(() => {
    fetchInsurances();
    fetchEmployees();
    
    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    setFilteredInsurances(insurances);
  }, [insurances]);

  // Scroll detection
  useEffect(() => {
    const checkScroll = () => {
      if (tableContainerRef.current) {
        const { scrollWidth, clientWidth } = tableContainerRef.current;
        const hasScroll = scrollWidth > clientWidth;
        setHasHorizontalScroll(hasScroll);
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [insurances, filteredInsurances]);

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [insurances, filteredInsurances]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleRowClick = (insurance) => {
    setSelectedInsurance(insurance);
  };

  const handleShowAddModal = () => {
    setSelectedInsurance(null);
    setShowAddModal(true);
  };

  const handleEditInsurance = () => {
    if (selectedInsurance) {
      setShowEditModal(true);
    }
  };

  const downloadExcel = () => {
    const dataToExport = workers.flatMap(worker => 
      worker.projects.map(project => {
        const key = `${worker.id}-${project.projectId}`;
        const projectPayments = payments[key] || [];
        
        // Create main row for worker-project
        const mainRow = {
          'Çalışan Adı': `${worker.name} ${worker.surname}`,
          'Proje': project.projectTitle,
          'Rol': project.role,
          'Tarife Tipi': isAdmin ? (project.rate?.type || '') : '',
          'Tarife Değeri': isAdmin ? (project.rate?.value || '') : '',
          'Atanma Tarihi': project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || 
                          (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : ''),
          'Visit Sayısı': visitCounts[key] || 0,
          'Toplam Gider': formatCurrency(expenses[key] || 0)
        };

        // If no payments, return just the main row
        if (projectPayments.length === 0) {
          return [mainRow];
        }

        // Create payment detail rows
        const paymentRows = projectPayments.map((payment, index) => ({
          'Çalışan Adı': index === 0 ? `${worker.name} ${worker.surname}` : '',
          'Proje': index === 0 ? project.projectTitle : '',
          'Rol': index === 0 ? project.role : '',
          'Tarife Tipi': index === 0 && isAdmin ? (project.rate?.type || '') : '',
          'Tarife Değeri': index === 0 && isAdmin ? (project.rate?.value || '') : '',
          'Atanma Tarihi': index === 0 ? (project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || 
                          (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '')) : '',
          'Visit Sayısı': index === 0 ? (visitCounts[key] || 0) : '',
          'Toplam Gider': index === 0 ? formatCurrency(expenses[key] || 0) : '',
          'Ödeme Tarihi': new Date(payment.paymentDate).toLocaleDateString('tr-TR'),
          'Ödeme Tipi': payment.paymentType,
          'Ödeme Tutarı': formatCurrency(payment.amount),
          'Açıklama': payment.description
        }));

        return paymentRows;
      })
    ).flat();

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Set column widths
    const columnWidths = [
      { wch: 20 }, // Çalışan Adı
      { wch: 20 }, // Proje
      { wch: 15 }, // Rol
      { wch: 15 }, // Tarife Tipi
      { wch: 15 }, // Tarife Değeri
      { wch: 15 }, // Atanma Tarihi
      { wch: 12 }, // Visit Sayısı
      { wch: 15 }, // Toplam Gider
      { wch: 15 }, // Ödeme Tarihi
      { wch: 15 }, // Ödeme Tipi
      { wch: 15 }, // Ödeme Tutarı
      { wch: 30 }  // Açıklama
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');
    
    XLSX.writeFile(workbook, 'Çalışan_Listesi.xlsx');
    
    toast.success('Excel dosyası başarıyla indirildi');
  };

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);

    let filtered = insurances;
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) {
        if (key === 'hasBES') {
          filtered = filtered.filter(insurance =>
            String(insurance[key]) === newFilters[key]
          );
        } else if (key.includes('.')) {
          // Nested property için (örn: accidentInsurance.startDate)
          const [parent, child] = key.split('.');
          filtered = filtered.filter(insurance =>
            insurance[parent]?.[child]?.toLowerCase().includes(newFilters[key].toLowerCase())
          );
        } else {
          filtered = filtered.filter(insurance =>
            String(insurance[key])
              ?.toLowerCase()
              .includes(newFilters[key].toLowerCase())
          );
        }
      }
    });

    // Eğer filtrelenmiş veri 1 satır ise, boş bir satır ekle
    if (filtered.length === 1) {
      filtered.push({
        ...filtered[0],
        id: 'empty-row',
        isEmpty: true
      });
    }

    setFilteredInsurances(filtered);
  };

  const handleDeleteInsurance = async () => {
    if (!selectedInsurance) return;

    try {
      await deleteDoc(doc(db, 'Insurances', selectedInsurance.id));
      toast.success('Sigorta başarıyla silindi');
      setSelectedInsurance(null);
      fetchInsurances();
    } catch (error) {
      console.error('Error deleting insurance:', error);
      toast.error('Sigorta silinirken bir hata oluştu');
    }
  };

  // Tablo dışına tıklanınca seçimi sıfırla
  const handlePageClick = (e) => {
    // Eğer tıklanan yer tablo veya butonlar değilse seçimi sıfırla
    if (!e.target.closest('table') && !e.target.closest('button')) {
      setSelectedInsurance(null);
    }
  };

  const handleEmployeeClick = async (worker, project) => {
    try {
      // Query visits collection for this specific employee and project
      const visitsRef = collection(db, 'Visits');
      const visitsQuery = query(
        visitsRef,
        where('employeeId', '==', worker.id),
        where('projectId', '==', project.projectId)
      );
      
      const visitsSnapshot = await getDocs(visitsQuery);
      
      setSelectedEmployee({
        id: worker.id,
        name: `${worker.name} ${worker.surname}`,
        project: {
          id: project.projectId,
          title: project.projectTitle
        },
        visits: visitsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      });
      
      if (visitsSnapshot.empty) {
        toast.info('Bu çalışanın visit\'i bulunamadı.');
      }
    } catch (error) {
      console.error('Error fetching employee visits:', error);
      toast.error('Ziyaret bilgileri alınırken bir hata oluştu.');
    }
  };

  // New function to fetch related data in bulk
  const fetchBulkRelatedData = async (workersData) => {
    if (!workersData || workersData.length === 0) {
      setVisitCounts({});
      setExpenses({});
      setPayments({});
      setTotalVisits(0);
      setTotalExpenses(0);
      return;
    }

    setBulkDataLoading(true); // Start loading indicator for bulk data
    try {
      // Fetch all Visits, Salaries, and Payments in parallel
      const [visitsSnapshot, salariesSnapshot, paymentsSnapshot] = await Promise.all([
        getDocs(collection(db, 'Visits')),
        getDocs(collection(db, 'Salaries')),
        getDocs(collection(db, 'Payments'))
      ]);

      // --- Process fetched data ---

      const visitCountMap = {};
      const expensesMap = {};
      const paymentsMap = {};
      let totalVisitCount = 0;
      let totalExpensesAmount = 0;

      // Create helper maps for faster lookups
      const allVisits = visitsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const allSalaries = salariesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const allPayments = paymentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Process data for each worker and project
      workersData.forEach(worker => {
        worker.projects.forEach(project => {
          const key = `${worker.id}-${project.projectId}`;
          
          // Calculate Visit Counts
          const projectVisits = allVisits.filter(
            visit => visit.employeeId === worker.id && visit.projectId === project.projectId
          );
          const visitsCount = projectVisits.length;
          visitCountMap[key] = visitsCount;
          totalVisitCount += visitsCount;

          // Calculate Expenses
          let projectExpense = 0;
          
          // Salaries
          const workerSalaries = allSalaries.filter(salary => salary.employeeId === worker.id);
          workerSalaries.forEach(salaryData => {
            if (salaryData.payments && (!salaryData.projectId || salaryData.projectId === project.projectId)) {
              Object.values(salaryData.payments).forEach(payment => {
                const amount = typeof payment.amount === 'string'
                  ? Number(payment.amount.replace(/\./g, '').replace(',', '.'))
                  : payment.amount;
                if (!isNaN(amount)) projectExpense += amount;
              });
            }
          });

          // Other Payments
          const workerProjectPayments = allPayments.filter(
            payment => payment.employeeId === worker.id && payment.projectId === project.projectId
          );
          workerProjectPayments.forEach(paymentData => {
            const amount = typeof paymentData.paidAmount === 'string'
              ? Number(paymentData.paidAmount.replace(/\./g, '').replace(',', '.'))
              : paymentData.paidAmount;
            if (!isNaN(amount)) projectExpense += amount;
          });
          
          expensesMap[key] = projectExpense;
          totalExpensesAmount += projectExpense;

          // Aggregate Payments
          let projectPaymentsAggregated = [];

          // Salary Payments
          workerSalaries.forEach(salaryData => {
            if (salaryData.payments && (!salaryData.projectId || salaryData.projectId === project.projectId)) {
              Object.entries(salaryData.payments).forEach(([paymentId, payment]) => {
                const amount = typeof payment.amount === 'string'
                  ? Number(payment.amount.replace(/\./g, '').replace(',', '.'))
                  : payment.amount;
                if (!isNaN(amount)) {
                  projectPaymentsAggregated.push({
                    id: paymentId,
                    paymentDate: payment.date, // Assuming payment.date exists and is valid
                    paymentType: 'Maaş Ödemesi',
                    amount: amount,
                    description: payment.description || 'Maaş Ödemesi'
                  });
                }
              });
            }
          });

          // Other Payments
          workerProjectPayments.forEach(paymentData => {
            const amount = typeof paymentData.paidAmount === 'string'
              ? Number(paymentData.paidAmount.replace(/\./g, '').replace(',', '.'))
              : paymentData.paidAmount;
             if (!isNaN(amount)) {
               projectPaymentsAggregated.push({
                id: paymentData.id, // Use doc id
                paymentDate: paymentData.paymentDate, // Assuming paymentData.paymentDate exists
                paymentType: paymentData.paymentType || 'Diğer Ödeme',
                amount: amount,
                description: paymentData.description || ''
              });
             }
          });

          // Sort payments by date
          projectPaymentsAggregated.sort((a, b) => {
             // Ensure dates are valid before comparing
             const dateA = a.paymentDate?.toDate ? a.paymentDate.toDate() : new Date(a.paymentDate);
             const dateB = b.paymentDate?.toDate ? b.paymentDate.toDate() : new Date(b.paymentDate);
             // Handle invalid dates if necessary
             if (isNaN(dateA) || isNaN(dateB)) return 0; 
             return dateB - dateA; // Most recent first
          });
          paymentsMap[key] = projectPaymentsAggregated;
        });
      });

      setVisitCounts(visitCountMap);
      setExpenses(expensesMap);
      setPayments(paymentsMap);
      setTotalVisits(totalVisitCount);
      setTotalExpenses(totalExpensesAmount);

    } catch (error) {
      console.error('Error fetching bulk related data:', error);
      toast.error('Visit, Gider ve Ödeme verileri yüklenirken bir hata oluştu.');
      // Set empty states on error
      setVisitCounts({});
      setExpenses({});
      setPayments({});
      setTotalVisits(0);
      setTotalExpenses(0);
    } finally {
      setBulkDataLoading(false); // Finish loading indicator for bulk data
    }
  };

  // Replace center and worker fetching with single worker fetch
  useEffect(() => {
    const fetchAllWorkers = async () => {
      setLoading(true);
      try {
        const projectsRef = collection(db, 'Projects');
        const projectsSnapshot = await getDocs(projectsRef);
        
        let workersData = [];
        let projectsCount = 0;
        const workerMap = new Map(); // Use a Map for efficient worker lookup

        // Go through each project
        for (const projectDoc of projectsSnapshot.docs) {
          const projectData = projectDoc.data();
          const projectEmployees = projectData.employees || [];
          
          if (projectEmployees.length > 0) {
            projectsCount++;
          }
          
          projectEmployees.forEach(emp => {
            // Basic validation
            if (!emp.id || !emp.name || !emp.surname) {
              console.warn("Skipping employee with missing data in project:", projectDoc.id, emp);
              return; 
            }

            const projectInfo = {
              projectId: projectDoc.id,
              projectTitle: projectData.titleShort || 'N/A', // Handle missing title
              role: emp.role,
              rate: emp.rate,
              assignedAt: emp.assignedAt, // Keep original format (Timestamp or Date)
              centerName: emp.centerName
            };

            if (workerMap.has(emp.id)) {
              // Add project to existing worker
              workerMap.get(emp.id).projects.push(projectInfo);
            } else {
              // Add new worker to map
              workerMap.set(emp.id, {
                id: emp.id,
                name: emp.name,
                surname: emp.surname,
                projects: [projectInfo]
              });
            }
          });
        }
        
        // Convert map values to array
        workersData = Array.from(workerMap.values());

        setWorkers(workersData);
        setActiveProjects(projectsCount);
        setLoading(false); // Stop initial loading indicator

        // Fetch related data after workers are loaded
        if (workersData.length > 0) {
           fetchBulkRelatedData(workersData); // Fetch bulk data here
        } else {
           // Ensure states are reset if no workers found
           setVisitCounts({});
           setExpenses({});
           setPayments({});
           setTotalVisits(0);
           setTotalExpenses(0);
        }

      } catch (error) {
        console.error('Error fetching workers:', error);
        toast.error('Çalışan bilgileri yüklenirken hata oluştu!');
        setLoading(false);
        setBulkDataLoading(false); // Ensure this is false on error too
      }
    };

    fetchAllWorkers();
  }, []); // Only run once on component mount

  const toggleWorkerExpanded = (workerId) => {
    setExpandedWorkers((prev) => {
      const next = new Set(prev);
      if (next.has(workerId)) {
        next.delete(workerId);
      } else {
        next.add(workerId);
      }
      return next;
    });
  };

  // Arama/filtre proje bazında daraltıyorsa eşleşen projelerin görünmesi için
  // satırlar otomatik açılır
  const projectFiltersActive = Boolean(
    searchTerm ||
    filters.project ||
    filters.role ||
    filters.rateType ||
    filters.rateValue ||
    filters.assignedAt
  );

  const isWorkerExpanded = (workerId) =>
    projectFiltersActive || expandedWorkers.has(workerId);

  const getWorkerTotals = (worker) =>
    worker.projects.reduce(
      (acc, project) => {
        const key = `${worker.id}-${project.projectId}`;
        acc.visits += visitCounts[key] || 0;
        acc.expense += expenses[key] || 0;
        return acc;
      },
      { visits: 0, expense: 0 }
    );

  const getWorkerRoles = (worker) =>
    [...new Set(worker.projects.map((p) => p.role).filter(Boolean))];

  const toggleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' }
    );
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className={styles.eaSortIdle} />;
    return sortConfig.dir === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const formatAssignedDate = (assignedAt) =>
    assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') ||
    (assignedAt instanceof Date ? assignedAt.toLocaleDateString('tr-TR') : '-');

  const workerInitials = (worker) =>
    `${(worker.name || '').charAt(0)}${(worker.surname || '').charAt(0)}`.toUpperCase() || '?';

  const formatCurrency = (amount) => {
    // Ensure amount is a number
    const numericAmount = typeof amount === 'number' ? amount : Number(amount);
    if (isNaN(numericAmount)) {
      return '0,00 ₺'; // Return default or error string
    }
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericAmount) + ' ₺';
  };

  const calculateTotalRate = (workerId) => {
    // Find the worker
    const worker = workers.find(w => w.id === workerId);
    if (!worker) return 0;
    
    // Calculate total rate from all projects
    let totalRate = 0;
    worker.projects.forEach(project => {
      if (project.rate && project.rate.value) {
        // Convert to number if it's a string
        const rateValue = typeof project.rate.value === 'string'
          ? parseFloat(project.rate.value.replace(/\./g, '').replace(',', '.'))
          : project.rate.value;
        
        if (!isNaN(rateValue)) {
          totalRate += rateValue;
        }
      }
    });
    
    return totalRate;
  };

  // Apply dark mode class to document body
  useEffect(() => {
    // Apply dark mode to document body based on isDarkMode
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Update filtering logic
  useEffect(() => {
    let filtered = [...workers]; // Create a copy of workers array

    // Apply search term first
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLocaleLowerCase('tr-TR').trim();
      
      filtered = filtered.map(worker => {
        const workerName = `${worker.name || ''} ${worker.surname || ''}`.toLocaleLowerCase('tr-TR').trim();
        const workerNameMatches = workerName.includes(lowercasedSearch);
        
        // If worker name matches, return ALL their projects
        if (workerNameMatches) {
          return worker;
        }
        
        // Otherwise, filter projects that match search term
        const matchingProjects = worker.projects.filter(project => {
          const projectMatches = safeToString(project.projectTitle).includes(lowercasedSearch);
          const roleMatches = safeToString(project.role).includes(lowercasedSearch);
          
          return projectMatches || roleMatches;
        });

        // If there are matching projects, return worker with those projects
        if (matchingProjects.length > 0) {
          return {
            ...worker,
            projects: matchingProjects
          };
        }
        
        return null; // Return null for workers with no matches
      }).filter(Boolean); // Remove null entries
    }

    // Apply column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        filtered = filtered.map(worker => {
          // Filter projects based on the column filter
          const matchingProjects = worker.projects.filter(project => {
            switch(key) {
              case 'employee':
                const fullName = `${safeToString(worker.name)} ${safeToString(worker.surname)}`;
                return fullName.includes(safeToString(filters[key]));
              
              case 'project':
                return safeToString(project.projectTitle).includes(safeToString(filters[key]));
              
              case 'role':
                return safeToString(project.role).includes(safeToString(filters[key]));
              
              case 'rateType':
                return safeToString(project.rate?.type) === safeToString(filters[key]);
              
              case 'rateValue':
                const rateValue = safeToString(project.rate?.value);
                return rateValue.includes(safeToString(filters[key]));
              
              case 'assignedAt':
                if (!project.assignedAt) return false;
                let assignedDate;
                try {
                  assignedDate = project.assignedAt.toDate 
                    ? project.assignedAt.toDate().toLocaleDateString('tr-TR')
                    : new Date(project.assignedAt).toLocaleDateString('tr-TR');
                } catch (error) {
                  assignedDate = '';
                }
                return safeToString(assignedDate).includes(safeToString(filters[key]));
              
              case 'visitCount':
                const visitKey = `${worker.id}-${project.projectId}`;
                return safeToString(visitCounts[visitKey] || 0).includes(safeToString(filters[key]));
              
              case 'totalExpense':
                const expenseKey = `${worker.id}-${project.projectId}`;
                return safeToString(expenses[expenseKey] || 0).includes(safeToString(filters[key]));
              
              default:
                return true;
            }
          });

          // If there are matching projects, return a new worker object with only those projects
          if (matchingProjects.length > 0) {
            return {
              ...worker,
              projects: matchingProjects
            };
          }
          return null; // Return null for workers with no matching projects
        }).filter(Boolean); // Remove null entries
      }
    });

    setFilteredWorkers(filtered);
  }, [workers, filters, searchTerm, visitCounts, expenses]);

  // Filtrelenmiş çalışanları seçili sütuna göre sırala
  const sortedWorkers = useMemo(() => {
    const arr = [...filteredWorkers];
    const dir = sortConfig.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (sortConfig.key === 'name') {
        return (
          dir *
          `${a.name || ''} ${a.surname || ''}`.localeCompare(
            `${b.name || ''} ${b.surname || ''}`,
            'tr'
          )
        );
      }
      if (sortConfig.key === 'projects') {
        return dir * (a.projects.length - b.projects.length);
      }
      const totalsA = getWorkerTotals(a);
      const totalsB = getWorkerTotals(b);
      if (sortConfig.key === 'visits') return dir * (totalsA.visits - totalsB.visits);
      return dir * (totalsA.expense - totalsB.expense);
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredWorkers, sortConfig, visitCounts, expenses]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Çalışan Analizi
            </h1>
            <div className={styles.headerButtons}>
              {/* Remove toggle button from here */}
              {/* 
              <button 
                className={`${styles.toggleFilterButton} ${showColumnFilters ? styles.activeToggle : ''}`}
                onClick={() => setShowColumnFilters(prev => !prev)}
                title={showColumnFilters ? 'Filtreleri Gizle' : 'Filtreleri Göster'}
              >
                <FaFilter />
              </button>
              */}
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{workers.length}</p>
                  <h3 className={styles.cardTitle}>Toplam Çalışan</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaProjectDiagram />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeProjects}</p>
                  <h3 className={styles.cardTitle}>Aktif Proje</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.visitsCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaCalendarAlt />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{totalVisits}</p>
                  <h3 className={styles.cardTitle}>Toplam Visit</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.expensesCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaMoneyBillWave />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{formatCurrency(totalExpenses)}</p>
                  <h3 className={styles.cardTitle}>Toplam Gider</h3>
                </div>
              </div>
            </div>
          </div>
          {/* Container for global search and filter toggle */}
          <div className={styles.searchAndFilterControls}>
            {/* Add global search bar back */}
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Genel Arama (İsim, Proje, Rol vb.)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button 
                  className={styles.clearSearch} 
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        </div>

        {selectedEmployee && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedEmployeeInfo}>
              <h3 className={styles.selectedName}>
                {selectedEmployee.name}
              </h3>
              <span className={`${styles.projectBadge} ${styles.statusBadge}`}>
                {selectedEmployee.project.title}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button 
                className={`${styles.actionButton} ${styles.fteButton}`} 
                onClick={() => {
                  setSelectedEmployeeFTE(selectedEmployee);
                  setShowFTEModal(true);
                }}
              >
                <FaMoneyBillWave className={styles.actionIcon} />
                <span>FTE Yönetimi</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${styles.statisticsButton}`} 
                onClick={() => {
                  navigate('/employee-statistics', {
                    state: {
                      employee: {
                        id: selectedEmployee.id,
                        name: selectedEmployee.name
                      },
                      dateRange: {
                        start: '',
                        end: ''
                      }
                    }
                  });
                }}
              >
                <FaChartLine className={styles.actionIcon} />
                <span>İstatistikler</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${styles.visitsButton}`} 
                onClick={() => setShowVisitsModal(true)}
              >
                <FaCalendarAlt className={styles.actionIcon} />
                <span>Visit Detayları</span>
              </button>
              
              <button 
                className={`${styles.actionButton} ${styles.expensesButton} ${showTariffDetails ? styles.active : ''}`} 
                onClick={() => setShowTariffDetails(!showTariffDetails)}
              >
                <FaMoneyBillWave className={styles.actionIcon} />
                <span>Tarife Toplamı</span>
              </button>
              
              {/* <button 
                className={`${styles.actionButton} ${styles.projectsButton}`} 
                onClick={() => {
                  // Navigate to project details view or show project modal
                  // You can implement this based on your needs
                  toast.info('Proje detayları gösteriliyor');
                }}
              >
                <FaProjectDiagram className={styles.actionIcon} />
                <span>Proje Detayları</span>
              </button> */}
            </div>
          </div>
        )}

        {selectedEmployee && showTariffDetails && (
          <TariffDetails 
            worker={workers.find(w => w.id === selectedEmployee.id)} 
            isDarkMode={isDarkMode} 
          />
        )}

        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p className={styles.loadingText}>Çalışan verileri yükleniyor...</p>
          </div>
        ) : (
          <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
            {/* Add a loading indicator for bulk data fetching */}
            {bulkDataLoading && (
              <div className={styles.loadingContainer} style={{ padding: '1rem', marginBottom: '1rem' }}>
                 <div className={styles.loadingSpinner} style={{ width: '24px', height: '24px', border_width: '3px' }}></div>
                 <p className={styles.loadingText} style={{ fontSize: '0.9rem' }}>Detaylar yükleniyor...</p>
              </div>
            )}
            <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
              <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
                {showLeftScroll && (
                  <button
                    className={`${styles.scrollButton} ${styles.scrollLeft}`}
                    onClick={() => scrollTable('left')}
                  >
                    <FaChevronLeft />
                  </button>
                )}
                {showRightScroll && (
                  <button
                    className={`${styles.scrollButton} ${styles.scrollRight}`}
                    onClick={() => scrollTable('right')}
                  >
                    <FaChevronRight />
                  </button>
                )}
                <div
                  ref={tableContainerRef}
                  className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                  onScroll={handleScroll}
                >
                  <table className={`${styles.table} ${styles.eaGroupedTable} ${isDarkMode ? 'dark-mode' : ''}`}>
                    <thead>
                      <tr>
                        <th className={styles.eaExpandTh} aria-label="Detay" />
                        <th className={styles.eaSortableTh} onClick={() => toggleSort('name')}>
                          Çalışan {sortIcon('name')}
                          <span onClick={(e) => e.stopPropagation()}>
                            <TableFilter
                              column="employee"
                              type="text"
                              onFilter={handleFilter}
                              currentValue={filters.employee || ''}
                            />
                          </span>
                        </th>
                        <th className={styles.eaSortableTh} onClick={() => toggleSort('projects')}>
                          Projeler {sortIcon('projects')}
                          <span onClick={(e) => e.stopPropagation()}>
                            <TableFilter
                              column="project"
                              type="text"
                              onFilter={handleFilter}
                              currentValue={filters.project || ''}
                            />
                          </span>
                        </th>
                        <th>
                          Roller
                          <TableFilter
                            column="role"
                            type="text"
                            onFilter={handleFilter}
                            currentValue={filters.role || ''}
                          />
                        </th>
                        <th className={styles.eaSortableTh} onClick={() => toggleSort('visits')}>
                          Toplam Visit {sortIcon('visits')}
                        </th>
                        <th className={styles.eaSortableTh} onClick={() => toggleSort('expense')}>
                          Toplam Gider {sortIcon('expense')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWorkers.length > 0 ? (
                        sortedWorkers.map((worker) => {
                          const expanded = isWorkerExpanded(worker.id);
                          const totals = getWorkerTotals(worker);
                          const roles = getWorkerRoles(worker);
                          return (
                            <React.Fragment key={worker.id}>
                              <tr
                                className={`${styles.eaMasterRow} ${expanded ? styles.eaMasterRowOpen : ''}`}
                                onClick={() => toggleWorkerExpanded(worker.id)}
                              >
                                <td className={styles.eaExpandCell}>
                                  {expanded ? <FaChevronUp /> : <FaChevronDown />}
                                </td>
                                <td>
                                  <span className={styles.eaEmployeeCell}>
                                    <span className={styles.eaAvatar}>{workerInitials(worker)}</span>
                                    <span className={styles.eaEmployeeName}>
                                      {`${worker.name || ''} ${worker.surname || ''}`.trim()}
                                    </span>
                                  </span>
                                </td>
                                <td>
                                  <span className={styles.eaCountBadge}>
                                    {worker.projects.length} proje
                                  </span>
                                </td>
                                <td>
                                  <span className={styles.eaRoleChips}>
                                    {roles.slice(0, 2).map((role) => (
                                      <span key={role} className={styles.eaRoleChip}>{role}</span>
                                    ))}
                                    {roles.length > 2 && (
                                      <span
                                        className={styles.eaRoleChipMore}
                                        title={roles.slice(2).join(', ')}
                                      >
                                        +{roles.length - 2}
                                      </span>
                                    )}
                                    {roles.length === 0 && '-'}
                                  </span>
                                </td>
                                <td className={styles.eaNumCell}>{totals.visits}</td>
                                <td className={styles.eaNumCell}>{formatCurrency(totals.expense)}</td>
                              </tr>
                              {expanded && (
                                <tr className={styles.eaDetailRow}>
                                  <td colSpan={6}>
                                    <table className={styles.eaSubTable}>
                                      <thead>
                                        <tr>
                                          <th>Proje</th>
                                          <th>Rol</th>
                                          {isAdmin && <th>Tarife Tipi</th>}
                                          {isAdmin && <th>Tarife Değeri</th>}
                                          <th>Atanma Tarihi</th>
                                          <th>Visit</th>
                                          <th>Gider</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {worker.projects.map((project) => {
                                          const dataKey = `${worker.id}-${project.projectId}`;
                                          const isSelected =
                                            selectedEmployee &&
                                            selectedEmployee.id === worker.id &&
                                            selectedEmployee.project?.id === project.projectId;
                                          return (
                                            <tr
                                              key={dataKey}
                                              className={`${styles.eaSubRow} ${isSelected ? styles.eaSubRowSelected : ''}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEmployeeClick(worker, project);
                                              }}
                                              title="Bu proje ataması için işlem panelini aç"
                                            >
                                              <td className={styles.eaProjectCell}>{project.projectTitle}</td>
                                              <td>{project.role || '-'}</td>
                                              {isAdmin && <td>{project.rate?.type || '-'}</td>}
                                              {isAdmin && <td>{project.rate?.value ?? '-'}</td>}
                                              <td>{formatAssignedDate(project.assignedAt)}</td>
                                              <td className={styles.eaNumCell}>{visitCounts[dataKey] || 0}</td>
                                              <td className={styles.eaNumCell}>
                                                {formatCurrency(expenses[dataKey] || 0)}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className={styles.noDataCell}>
                            <div className={styles.noData}>
                              <FaUsers className={styles.noDataIcon} />
                              <p>Filtrelere uygun veri bulunamadı</p>
                              {(Object.keys(filters).length > 0 || searchTerm) && (
                                <button className={styles.clearFiltersButton} onClick={() => {
                                  setFilters({});
                                  setSearchTerm('');
                                }}>
                                  Filtreleri Temizle
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        )}

        {selectedEmployee && (
          <ProjectVisitsModal
            show={showVisitsModal}
            onHide={() => {
              setShowVisitsModal(false);
              setSelectedEmployee(null);
            }}
            project={selectedEmployee.project}
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            visits={selectedEmployee.visits}
          />
        )}

        {selectedEmployeeFTE && (
          <FTEModal
            show={showFTEModal}
            onClose={() => {
              setShowFTEModal(false);
              setSelectedEmployeeFTE(null);
            }}
            employee={selectedEmployeeFTE}
            onSaved={async () => {
              // Refresh workers data without page reload
              setShowFTEModal(false);
              setSelectedEmployeeFTE(null);
              
              // Show loading toast
              const loadingToast = toast.loading('Veriler güncelleniyor...');
              
              try {
                // Re-fetch workers data
                const projectsRef = collection(db, 'Projects');
                const projectsSnapshot = await getDocs(projectsRef);
                
                const workerMap = new Map();
                let projectsCount = 0;

                for (const projectDoc of projectsSnapshot.docs) {
                  const projectData = projectDoc.data();
                  const projectEmployees = projectData.employees || [];
                  
                  if (projectEmployees.length > 0) {
                    projectsCount++;
                  }
                  
                  projectEmployees.forEach(emp => {
                    if (!emp.id || !emp.name || !emp.surname) {
                      return; 
                    }

                    const projectInfo = {
                      projectId: projectDoc.id,
                      projectTitle: projectData.titleShort || 'N/A',
                      role: emp.role,
                      rate: emp.rate,
                      assignedAt: emp.assignedAt,
                      centerName: emp.centerName
                    };

                    if (workerMap.has(emp.id)) {
                      workerMap.get(emp.id).projects.push(projectInfo);
                    } else {
                      workerMap.set(emp.id, {
                        id: emp.id,
                        name: emp.name,
                        surname: emp.surname,
                        projects: [projectInfo]
                      });
                    }
                  });
                }
                
                const workersData = Array.from(workerMap.values());
                setWorkers(workersData);
                
                toast.update(loadingToast, {
                  render: 'FTE bilgileri başarıyla güncellendi!',
                  type: 'success',
                  isLoading: false,
                  autoClose: 3000
                });
              } catch (error) {
                console.error('Error refreshing workers:', error);
                toast.update(loadingToast, {
                  render: 'Veriler güncellenirken hata oluştu',
                  type: 'error',
                  isLoading: false,
                  autoClose: 3000
                });
              }
            }}
          />
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
        />
      </div>
    </div>
  );
};

export default EmployeeAnalysis;